import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d7d056em3fsfopf23kcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 mb-4">
            Get In Touch
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
            Ready to Get Your Offer?
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            Whether you&apos;re a property seller or a real estate agent, we&apos;d love to hear from you. Reach out and we&apos;ll get back to you quickly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-8">Contact Information</h3>
            <div className="space-y-6 mb-10">
              {[
                {
                  icon: 'ri-phone-line',
                  label: 'Phone',
                  value: '0411 499 997',
                  href: 'tel:0411499997',
                },
                {
                  icon: 'ri-mail-line',
                  label: 'Email',
                  value: 'info@readybuyergroup.com.au',
                  href: 'mailto:info@readybuyergroup.com.au',
                },
                {
                  icon: 'ri-map-pin-2-line',
                  label: 'Address',
                  value: 'Ground Floor, 470 St Kilda Road, Melbourne VIC 3004',
                  href: 'https://maps.google.com/?q=470+St+Kilda+Road+Melbourne+VIC+3004',
                },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'nofollow noreferrer' : undefined}
                  className="flex items-start gap-4 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0 group-hover:bg-teal-100 transition-colors">
                    <i className={`${item.icon} text-teal-700 text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{item.label}</div>
                    <div className="text-gray-800 font-medium text-sm group-hover:text-teal-700 transition-colors">
                      {item.value}
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-56">
              <iframe
                title="ReadyBuyer Group Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.8354345093747!2d144.97592!3d-37.8401!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad667b0b4b3b3b3%3A0x0!2s470+St+Kilda+Rd%2C+Melbourne+VIC+3004!5e0!3m2!1sen!2sau!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Company Info */}
            <div className="mt-6 p-5 bg-white rounded-xl">
              <div className="text-sm text-gray-500 space-y-1">
                <div className="font-semibold text-gray-700">Services Marketplace Pty Ltd</div>
                <div>ABN: 62 631 153 216</div>
                <div>Ground Floor, 470 St Kilda Road, Melbourne VIC 3004</div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Thank you for reaching out. Our team will get back to you shortly.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-6">Send Us a Message</h3>

                {/* Role Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">I am a:</label>
                  <div className="flex gap-4">
                    {['Property Seller', 'Real Estate Agent'].map((role) => (
                      <label key={role} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="role"
                          value={role}
                          defaultChecked={role === 'Property Seller'}
                          className="accent-teal-700"
                        />
                        <span className="text-sm text-gray-700">{role}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Jane Smith"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="04XX XXX XXX"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="jane@example.com"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Property Address (if applicable)</label>
                  <input
                    type="text"
                    name="property_address"
                    placeholder="123 Example St, Suburb VIC 3000"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message *
                    <span className="text-gray-400 font-normal ml-2 text-xs">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your property or how we can help..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  ></textarea>
                  {charCount > 500 && (
                    <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full py-4 bg-teal-700 hover:bg-teal-800 disabled:bg-teal-400 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  No obligation &bull; We respond within 1 business day
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
